/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';

import { MedliSearchView } from '../componentViews/SearchView';
import { MedliStaticListView } from '../componentViews/StaticListView';

import { useHealthEntryForActiveProfile } from '../../hooks/useHealthEntry';
import { useMedliList, useMedliVaccinationList } from '../../hooks/useMedli';
import { useNavigate } from '../../hooks/useNavigate';

import RouteNames from '../../appAuthorized/RouteNames';
import { eMedliCategoryName } from '../../../constants/MedliNames';
import HealthEntryCategoryNames from '../../../constants/HealthEntryCategoryNames';
import { HealthEntryData } from '../../../models/HealthEntry';

const BACKPACK_GROUP_NAME = 'backpack';

interface GeneralProps {
  match: any;
}
const HealthEntryVaccination: React.FC = () => {
  const [, navActions] = useNavigate();
  const [{ vaccinationList }] = useMedliVaccinationList();
  const [, heActions] = useHealthEntryForActiveProfile();

  return (
    <MedliStaticListView
      suggestedResults={vaccinationList}
      title="vaccinationSearch.search.title.text"
      medliCategory={eMedliCategoryName.vaccination}
      onGoBack={() => {
        navActions.goBack();
      }}
      selectSearchResult={(medliTermData) => {
        const newHE = heActions.instantiateNewHealthEntryFromSearch(HealthEntryCategoryNames.vaccination, null, medliTermData);

        heActions.apiCreate(newHE.data())
          .then((createdHealthEntry: HealthEntryData) => {
            navActions.goTo(RouteNames.healthEntryNew(createdHealthEntry.id));
          });
      }}
    />
  );
};

export const HealthEntryCreate: React.FC<GeneralProps> = (props) => {
  const medliCategory = props.match.params.medliCategory;

  const [, heActions] = useHealthEntryForActiveProfile();
  const [, navActions] = useNavigate();
  const [medliListData] = useMedliList(medliCategory);

  const suggestionResults = medliListData.list.reduce((acc, v) => {
    const { groups } = v;
    const includesBackpack = groups.includes(BACKPACK_GROUP_NAME);

    if (includesBackpack && groups.length === 1) {
      acc.backpack.push(v);
    }
    if (!includesBackpack || groups.length > 1) {
      acc.group.push(v);
    }
    return acc;
  }, { group: [], backpack: [] });

  const suggestedResults = suggestionResults.group.length === 0 ? [
    {
      results: suggestionResults.backpack,
      titleId: 'search.suggestions.heading',
    },
  ] : [
    {
      results: suggestionResults.group,
      titleId: 'search.suggestions.group.heading',
    },
    {
      results: suggestionResults.backpack,
      titleId: 'search.suggestions.backpack.heading',
    },
  ];

  let category = '';
  let queryPlaceholder = '';
  let medliSearchCategory = null;
  let createValueNext = false;

  switch (medliCategory) {
    case eMedliCategoryName.condition:
      queryPlaceholder = 'conditionSearch.search.field.hint';
      category = HealthEntryCategoryNames.condition;
      medliSearchCategory = eMedliCategoryName.condition;
      break;
    case eMedliCategoryName.procedure:
      queryPlaceholder = 'procedureSearch.search.field.hint';
      category = HealthEntryCategoryNames.procedure;
      medliSearchCategory = eMedliCategoryName.procedure;
      break;
    case eMedliCategoryName.medication:
      queryPlaceholder = 'medicationSearch.search.field.hint';
      category = HealthEntryCategoryNames.medication;
      medliSearchCategory = eMedliCategoryName.medication;
      break;
    case eMedliCategoryName.otherTreatment:
      queryPlaceholder = 'treatmentSearch.search.field.hint';
      category = HealthEntryCategoryNames.otherTreatment;
      medliSearchCategory = eMedliCategoryName.otherTreatment;
      break;
    case eMedliCategoryName.medicationAllergy:
      queryPlaceholder = 'allergyMedicationSearch.search.field.hint';
      category = HealthEntryCategoryNames.allergy;
      medliSearchCategory = eMedliCategoryName.medicationName;
      break;
    case eMedliCategoryName.foodAllergy:
      queryPlaceholder = 'allergyFoodSearch.search.field.hint';
      category = HealthEntryCategoryNames.allergy;
      medliSearchCategory = eMedliCategoryName.food;
      break;
    case eMedliCategoryName.allergy:
      queryPlaceholder = 'allergyOtherSearch.search.field.hint';
      category = HealthEntryCategoryNames.allergy;
      medliSearchCategory = eMedliCategoryName.allergy;
      break;
    case eMedliCategoryName.vaccination:
      return <HealthEntryVaccination />;
    case eMedliCategoryName.lab:
      queryPlaceholder = 'labSearch.search.field.hint';
      category = HealthEntryCategoryNames.lab;
      medliSearchCategory = eMedliCategoryName.lab;
      createValueNext = true;
      break;
    case eMedliCategoryName.measurement:
      queryPlaceholder = 'measurementSearch.search.field.hint';
      category = HealthEntryCategoryNames.measurement;
      medliSearchCategory = eMedliCategoryName.measurement;
      createValueNext = true;
      break;
    case eMedliCategoryName.gene:
      queryPlaceholder = 'geneSearch.search.field.hint';
      category = HealthEntryCategoryNames.geneticResult;
      medliSearchCategory = eMedliCategoryName.gene;
      createValueNext = true;
      break;
    default:
      break;
  }

  if (!medliCategory) { return null; }

  return (
    <MedliSearchView
      medliCategory={medliCategory}
      medliSearchCategory={medliSearchCategory}
      hasSuggestions={true}
      queryPlaceholder={queryPlaceholder}
      suggestedResults={suggestedResults}
      onSelectResult={(selectedMedliSearchResult) => {
        return new Promise(() => {
          const medliTerm = selectedMedliSearchResult.searchResult.data;
          const newHealthEntry = heActions.instantiateNewHealthEntryFromSearch(category, medliSearchCategory, medliTerm);
          // create new health entry
          heActions.apiCreate(newHealthEntry.data())
            .then((he: any) => {
              const heId = he.id;
              // if health entry value, go to new hev view
              if (createValueNext) {
                heActions.apiAddHealthEntryValue(he)
                  .then(({ newId }) => {
                    navActions.replace(RouteNames.healthEntryNew(heId));
                    navActions.goTo(RouteNames.healthEntryValueNew(heId, newId));
                  });
              } else {
                navActions.replace(RouteNames.healthEntryNew(heId));
              }
            }).catch();
        });
      }}
      onGoBack={() => {
        navActions.goBack(RouteNames.healthInfo());
      }}
    />
  );
};
